import { C1_DinSymbol } from "../../plan/C1_DinSymbol";
import { C1_EloItem_Type } from "../../types/C1_EloTypes";
import { C1_ELO_LIGHT_TYPE_LABELS } from "../../C1_uiLabels";
import { LightPaletteIcon } from "./C1_LightPaletteIcon";
import { itx } from "../../../../src/translationItx/itx";
import { channelTypeHirarchie } from "../../../../src/models/ChannelType";

import {
  getLightProductDescription,
  getLightProductImageUrl,
  getLightSymbolOutlineRadiusRatio,
  getLightProductIdsForUsage,
} from "../../data/C1_ProductDefinitions";
import { ChannelType } from "../../types/C1_EloTypes";
import type { C1_FurnitureDefinition, C1_FurnitureRendererProps } from "../../types/C1_FurnitureTypes";
import { FurnitureCategory } from "../../types/C1_ModelTypes";
import { lightGroupCount, lightGroupRows } from "../C1_lightGroup";
import { normalizeEloItemScale } from "../../plan/C1_eloItemScale";
import c1_state from "../../model/C1_State";
import { useOnChangeWatcher } from "../../../../src/models/useOnChangeWatcher";
import { useContext } from "react";
import {
  C1_FurnitureSvg,
  C1_FurnitureVectorContext,
} from "../C1_FurnitureSvg";

function C1_LightGroupRenderer({
  width,
  height,
  PX,
  count,
  rows,
  effectiveRows,
  lightProductId,
  ownerRotation,
  ownerReflect,
  eloTree,
}: C1_FurnitureRendererProps) {
  useOnChangeWatcher(c1_state.editMode);
  useOnChangeWatcher(c1_state.settings);
  useOnChangeWatcher(c1_state.project.displayMode);
  const displayMode = c1_state.floorImageRenderOptions.value?.displayMode ?? c1_state.project.displayMode.value;
  const dinMode = displayMode === "din";
  useOnChangeWatcher(c1_state.floorImageRenderOptions);
  const vectorMode = useContext(C1_FurnitureVectorContext);
  // The array is the visual representation of one consumer in every edit mode.

  const columns = lightGroupCount(count);
  const finalRows = lightGroupRows(effectiveRows ?? rows);
  const totalCount = columns * finalRows;
  const imageUrl = c1_state.floorImageRenderOptions.value?.excludeEloImages
    ? undefined
    : getLightProductImageUrl(typeof lightProductId === "string" ? lightProductId : undefined);
  const imageSize = Math.max(24, Math.min(64, 0.65 * PX));
  const outlineRadiusRatio = getLightSymbolOutlineRadiusRatio(typeof lightProductId === "string" ? lightProductId : undefined);
  const symbolScale = normalizeEloItemScale((c1_state.floorImageRenderOptions.value?.eloItemScale ?? c1_state.settings.eloItemScale));
  const counterTransform = `scale(${ownerReflect ? -1 : 1} 1) rotate(${-Number(ownerRotation || 0)})`;
  // Real arrays use the consumer renderer for every technical symbol and for
  // the first design light at [0,0]. Palette previews retain all their images.
  const hasConsumer = Boolean(eloTree?.items.length);
  if (hasConsumer && displayMode !== "design") return null;

  const content = (
    <g>
      {Array.from({ length: totalCount }, (_, index) => {
        const row = Math.floor(index / columns);
        const column = index % columns;
        const x = columns <= 1 ? 0 : (column * width) / (columns - 1);
        const y = finalRows <= 1 ? 0 : (row * height) / (finalRows - 1);
        if (hasConsumer && index === 0) return null;
        return (
          <g key={index} transform={`translate(${x} ${y}) ${counterTransform} scale(${symbolScale})`}>
            {!dinMode && imageUrl && outlineRadiusRatio !== undefined && (
              <circle className="C1_svgLightGroupOutline" cx={0} cy={0} r={imageSize * outlineRadiusRatio}
                fill="#fff" fillOpacity={0.5} stroke="#94a3b8" strokeWidth={0.6} pointerEvents="visiblePainted" />
            )}
            {dinMode ? <C1_DinSymbol symbolType={C1_EloItem_Type.Light} x={-imageSize / 2} y={-imageSize / 2} width={imageSize} height={imageSize} color="#173f56" /> : imageUrl ? (
              <image
                href={imageUrl}
                x={-imageSize / 2}
                y={-imageSize / 2}
                width={imageSize}
                height={imageSize}
                preserveAspectRatio="xMidYMid meet"
                pointerEvents="visiblePainted"
              />
            ) : (
              <circle cx={0} cy={0} r={8} fill="#fff" stroke="#173f56" strokeWidth={2} pointerEvents="visiblePainted" />
            )}
          </g>
        );
      })}
    </g>
  );
  if (vectorMode) return content;

  return (
    <C1_FurnitureSvg
      PX={PX}
      rotation={0}
      width={width}
      height={height}
      viewBoxX={0}
      viewBoxY={0}
      viewBoxWidth={width}
      viewBoxHeight={height}
      originXPercentage={0}
      originYPercentage={0}
    >
      {content}
    </C1_FurnitureSvg>
  );
}

export const LightGroup: C1_FurnitureDefinition = {
  id: "lightGroup",
  description: { de: "Leuchten (Gruppe)", en: "Lights (group)" },
  category: FurnitureCategory.Electrical,
  defaultWidth: 1.5,
  defaultHeight: 1.5,
  originXPercentage: 0,
  originYPercentage: 0,
  autoRotate: true,
  grabsW: ({ values }) => lightGroupCount(values.count) > 1,
  grabsH: ({ values, wallDocked }) => !wallDocked && lightGroupRows(values.rows) > 1,
  icon: <LightPaletteIcon group />,
  properties: [
    { key: "count", get label() { return itx("count", "Anzahl"); }, type: "number", defaultValue: 3, min: 1, max: 100, step: 1 },
    {
      key: "rows",
      get label() { return itx("c1.ui.Anzahl_Reihen", "Anzahl Reihen"); },
      type: "number",
      defaultValue: 1,
      min: 1,
      max: 100,
      step: 1,
      visible: ({ wallDocked }) => !wallDocked,
    },
    {
      key: "channelType",
      get label() { return itx("c1.ui.Lichttyp", "Lichttyp"); },
      type: "select",
      defaultValue: ChannelType.Dimmer,
      options: channelTypeHirarchie("isLight")!.map((value) => ({
        value,
        get label() { return C1_ELO_LIGHT_TYPE_LABELS[value] ?? value; },
      })),
    },
    {
      key: "lightProductId",
      get label() { return itx("c1.ui.Bauform", "Bauform"); },
      type: "select",
      defaultValue: "symbol-light-ceiling",
      options: ({ wallDocked }) =>
        getLightProductIdsForUsage(wallDocked ? "indoorWall" : "indoorCeiling")
          .map((value) => ({ value, label: getLightProductDescription(value) })),
    },
  ],
  Renderer: C1_LightGroupRenderer,
};

export const OutdoorLightGroup: C1_FurnitureDefinition = {
  ...LightGroup,
  id: "outdoorLightGroup",
  description: { de: "Leuchten (Gruppe)", en: "Lights (group)" },
  category: FurnitureCategory.Outdoor,
  properties: LightGroup.properties.map((property) => {
    if (property.key === "channelType") {
      return { ...property, get label() { return property.label; }, defaultValue: ChannelType.Switch };
    }
    if (property.key === "lightProductId") {
      return {
        ...property,
        get label() { return property.label; },
        defaultValue: "outdoor-stand-bollard",
        options: () =>
          [
            ...getLightProductIdsForUsage("outdoorFloor"),
            ...getLightProductIdsForUsage("outdoorCeiling"),
          ].map((value) => ({
            value,
            label: getLightProductDescription(value),
          })),
      };
    }
    return property;
  }),
};

export default LightGroup;
